/**
 * PageTabs — Relay's standard page-level tab bar (product decision 2026-10-03,
 * track:backlog-cleanup-ux). Extracted from the Workspace page's tab bar so the
 * look is defined once: title-case labels, 13px --font-ui, a 2px --teal-dark
 * underline on the active tab, inactive tabs in --muted. Callers own the
 * surrounding padding.
 */
import { useRef, type KeyboardEvent } from "react";

export interface PageTab {
  id: string;
  label: string;
  count?: number;
}

export function PageTabs({
  tabs,
  active,
  onChange,
  label = "Page sections",
}: {
  tabs: PageTab[];
  active: string;
  onChange: (id: string) => void;
  /** Accessible name for the tablist. */
  label?: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (e.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    else return;
    e.preventDefault();
    onChange(tabs[next].id);
    refs.current[tabs[next].id]?.focus();
  };

  return (
    <div role="tablist" aria-label={label} style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {tabs.map((tab, i) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={(el) => { refs.current[tab.id] = el; }}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            style={{
              padding: "8px 16px",
              background: "none",
              border: "none",
              fontFamily: "var(--font-ui)",
              borderBottom: isActive ? "2px solid var(--teal-dark)" : "2px solid transparent",
              color: isActive ? "var(--teal-dark)" : "var(--muted)",
              fontWeight: isActive ? 600 : 500,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            {tab.label}
            {tab.count !== undefined ? ` (${tab.count})` : ""}
          </button>
        );
      })}
    </div>
  );
}
